import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { LexicalEditor as LexicalEditorType } from 'lexical';
import {
  ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability,
  ContactInteractionChannelResourceUnion,
  Mutation,
  MutationConversationSendFacebookMessageArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import {
  ActiveAiAgentBanner,
  useHasActiveAiAgent,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/active-ai-agent-banner';
import { ContactInteractionInputAreaCharacterCounter } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-area-character-counter';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { ContactInteractionsReplyModeInfoMessage } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interactions-reply-mode-message';
import { CustomEditor } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/custom-editor';
import {
  CustomEditorProps,
  useHandleReplyError,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/helpers';
import { CONVERSATION_SEND_FACEBOOK_MESSAGE } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/graphql-operations';
import { ReplyWindowBar } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/reply-window-bar';
import { useAuthorization } from 'src/hooks/use-authorization';

export const FacebookMessengerEditor = memo<CustomEditorProps>(
  ({ handleFetchNewMessageCompleted, channelAvailability, availabilityMessageRef }) => {
    const { messageValues, attachmentList, conversationId } = useContext(
      ContactInteractionInputManagerContext,
    );

    const { t } = useTranslation(['conversations']);
    const handleReplyError = useHandleReplyError();
    const hasActiveAiAgent = useHasActiveAiAgent();
    const { getFeatureFlag } = useAuthorization();

    const isFacebookUpdateFeatureEnabled = getFeatureFlag(
      'channel-facebook-messenger-update',
    );

    const isAiToolsFeatureEnabled = getFeatureFlag('AI');
    const isMessageTemplatesFeatureEnabled = getFeatureFlag(
      'contact-interaction-templates',
    );

    const [selectedResource, setSelectedResource] =
      useState<ContactInteractionChannelResourceUnion | null>(null);
    const [selectedResourceAvailability, setSelectedResourceAvailability] = useState<
      | ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability
      | undefined
    >(undefined);

    const [sendFacebookMessage, { loading: isSendingFacebookMessage }] = useMutation<
      Pick<Mutation, 'conversationSendFacebookMessage'>,
      MutationConversationSendFacebookMessageArgs
    >(CONVERSATION_SEND_FACEBOOK_MESSAGE, {
      onError: handleReplyError,
    });

    const handleSendMessage = useCallback(
      async (editor: LexicalEditorType) => {
        if (!conversationId || isSendingFacebookMessage) {
          return;
        }

        let commonVariables: Pick<
          MutationConversationSendFacebookMessageArgs,
          'conversationId' | 'message' | 'attachments' | 'resourceId'
        > = {
          conversationId,
          message: messageValues.message ?? '',
          attachments: [],
          resourceId: selectedResourceAvailability?.resourceId || '',
        };

        const attachments = attachmentList.map(({ url }) => ({
          s3UploadedUrl: url ?? '',
        }));

        if (attachments.length > 0) {
          commonVariables = {
            ...commonVariables,
            attachments,
          };
        }

        await sendFacebookMessage({
          variables: {
            ...commonVariables,
          },
          onCompleted: async ({ conversationSendFacebookMessage: data }) => {
            await handleFetchNewMessageCompleted({
              editor,
              where: {
                contactInteractionId: {
                  eq: Number(data.interaction.id),
                },
              },
            });
          },
        });
      },
      [
        isSendingFacebookMessage,
        conversationId,
        messageValues.message,
        attachmentList,
        selectedResourceAvailability,
        sendFacebookMessage,
        handleFetchNewMessageCompleted,
      ],
    );

    const isContactWindowOpen = useMemo(
      () =>
        !!selectedResourceAvailability &&
        selectedResourceAvailability.unavailableReasonCode !== 'CHAT_WINDOW_CLOSED',
      [selectedResourceAvailability],
    );

    const isInputEnabled = useMemo(
      () =>
        !hasActiveAiAgent &&
        (isFacebookUpdateFeatureEnabled ? isContactWindowOpen : true),
      [hasActiveAiAgent, isContactWindowOpen, isFacebookUpdateFeatureEnabled],
    );

    useEffect(() => {
      if (isFacebookUpdateFeatureEnabled && channelAvailability) {
        setSelectedResourceAvailability(
          channelAvailability.resourceToContactAddressAvailability?.find(
            (resource) => resource.resourceId === selectedResource?.id,
          ),
        );
      }
    }, [channelAvailability, isFacebookUpdateFeatureEnabled, selectedResource?.id]);

    useEffect(() => {
      if (!channelAvailability) {
        return;
      }
      const { resources, lastUsedResourceId } = channelAvailability;

      const defaultSelectedResource =
        resources.find(({ id }) => id === lastUsedResourceId) ?? resources[0] ?? null;

      setSelectedResource(defaultSelectedResource);
    }, [channelAvailability]);

    const replyWindowBar = useMemo(() => {
      return isFacebookUpdateFeatureEnabled &&
        isContactWindowOpen &&
        availabilityMessageRef?.current
        ? createPortal(
            <ReplyWindowBar
              expirationDate={selectedResourceAvailability?.expiresAt}
              type="FACEBOOK_MESSENGER"
            />,
            availabilityMessageRef.current,
          )
        : null;
    }, [
      availabilityMessageRef,
      isContactWindowOpen,
      isFacebookUpdateFeatureEnabled,
      selectedResourceAvailability?.expiresAt,
    ]);

    const topContent = useMemo(() => {
      if (hasActiveAiAgent) {
        return <ActiveAiAgentBanner />;
      }

      if (isContactWindowOpen || !isFacebookUpdateFeatureEnabled) {
        return null;
      }

      return (
        <>
          <ContactInteractionsReplyModeInfoMessage
            title={t(
              'conversations:messageInput.modes.reply.messages.facebook.messageWindowClosed.title',
            )}
            body={t(
              'conversations:messageInput.modes.reply.messages.facebook.messageWindowClosed.body',
            )}
            lggId="facebook-messenger-message-window-closed-info-message"
          />
        </>
      );
    }, [hasActiveAiAgent, isContactWindowOpen, t, isFacebookUpdateFeatureEnabled]);

    return (
      <>
        {replyWindowBar}
        <CustomEditor
          handleSubmission={handleSendMessage}
          isSubmitting={isSendingFacebookMessage}
          bottomContent={{
            visible: true,
          }}
          topContent={topContent}
          pluginConfig={{
            emojiPicker: { visible: true },
            messageAttachments: {
              visible: true,
            },
            aiTools: { visible: isAiToolsFeatureEnabled },
            messageTemplates: {
              visible: isMessageTemplatesFeatureEnabled,
            },
            inv360: { visible: true },
          }}
          isInputEnabled={isInputEnabled}
          rightRailLeadingContent={<ContactInteractionInputAreaCharacterCounter />}
        />
      </>
    );
  },
);
